import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import { DateNav } from "../shell/DateNav";
import { useHasLens } from "../session/capabilities";
import type { Period } from "../router/search";
import {
  useGetV1Kpi,
  useGetV1KpiSeries,
  useGetV1Series,
  type EnvelopeKpiData,
  type EnvelopeKpiSeriesData,
  type EnvelopeSeriesData,
  type Figure,
  type GetV1KpiSeriesMetric,
  type LossCascadeRow,
  type MetaPeriod,
  type PrVariant,
  type RowStatus,
} from "@solarkey/api-client";
import { EmptyState, ErrorState, Metric, Money, Skeleton, StateWord } from "@solarkey/ui";
import {
  KpiTrend,
  LossCascade,
  type CascadeRowStatus,
  type CascadeStep,
} from "@solarkey/charts";

/* ── Screen 17 (F1/F3): Site Performance, at site scope ──
   Every number on this screen comes from `/v1/kpi?scope=<site>`: the KPI tiles and the
   17-row loss waterfall, which is rendered through the shared LossCascade component so
   the cascade looks and reads the same here as on Home.

   Each tile carries a caret that opens its own daily trend in place below the tile row
   (UX-006 §5.16/§5.17, UX-007 §9), one open at a time. The trend is read from
   `/v1/kpi/series?metric=…&scope=<site>`: one engine-computed figure per COMPLETED day.
   A day the engine produced no figure for arrives WITHHELD — state word and reason, no
   value — and the chart breaks the line there. It is never plotted as zero, never
   interpolated across, and never dropped from the axis.

   What this screen still does NOT show, and why: there is no performance-index figure in
   the contract, no per-day loss ladder, and no downtime-by-cause seam. Those panels print
   a state word with the reason instead of a drawn series. A fabricated figure is worse
   than none (KPIDEF-001 3.2/6.1/9). The screen also derives nothing: shares, waterfall
   closure, index figures and any rolling or cumulative trend line belong to the engine,
   never the browser (CLAUDE.md rule 2). */

// The periods this screen offers (wireframe 17, annotation 1). `custom` is here
// now that the screen collects a range: the date navigator beside the tabs steps
// a window and writes `from`/`to`, so nothing is ever sent without the bounds it
// needs — which is what kept `custom` off this list before.
const PERIOD_TABS: { key: Period; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "mtd", label: "Month" },
  { key: "fy", label: "FY" },
  { key: "custom", label: "Custom" },
];

/** The six tiles, as the caret addresses them. */
type TileKey = "energy" | "pr" | "cuf" | "avail" | "yield" | "co2";

/* Which `/v1/kpi/series` metric each tile's caret opens.
   `avail` depends on the tile's own basis chip: the contract serves a daily series for
   the technical (plant) availability, but there is no grid-availability metric in the
   enum — so that chip's trend is withheld with the reason rather than quietly drawn
   from the technical series. */
function metricForTile(key: TileKey, availBasis: "PLANT" | "GRID"): GetV1KpiSeriesMetric | null {
  switch (key) {
    case "energy": return "energy";
    case "pr": return "pr";
    case "cuf": return "cuf";
    case "avail": return availBasis === "PLANT" ? "plant_availability" : null;
    case "yield": return "specific_yield";
    case "co2": return "co2";
  }
}

/* Which TELEMETRY signal a tile can be drawn from on the live day.
   Today has no completed day, so `/v1/kpi/series` has nothing for it — but the
   energy register is read right through the day, so that tile can show the curve
   to now ("till time") instead of an honest but empty panel.

   The others are genuinely not intraday quantities: PR, CUF, specific yield, the
   availabilities and CO2 are each computed over a WHOLE site-local day, so there
   is no partial-day value to plot. Those keep the panel that says so and offers
   the completed-day window instead; inventing a running ratio here would be a KPI
   formula in the browser (rule 2) and a different basis from the tile above it. */
const LIVE_DAY_SIGNAL: Partial<Record<TileKey, string>> = {
  energy: "energy_active_daily",
};

/** The line the trend panel names in its caption. One line: the server sends one. */
const DAILY_LINE_LABEL = "daily";

/** A chart worth reading: the panel is the width of the page, so it gets height
 *  to match rather than the component's compact default. */
const TREND_HEIGHT = 220;

/** What the trend panel calls itself for each tile. */
const TILE_TREND_TITLE: Record<TileKey, string> = {
  energy: "Energy delivered · daily",
  pr: "PR · daily",
  cuf: "CUF · daily",
  avail: "Availability · daily",
  yield: "Specific yield · daily",
  co2: "CO₂ avoided · daily",
};

/* The window the trend offers to widen to when the selected tab is the live day, which
   by definition has no completed KPI. It is only ever applied by an explicit click, and
   the panel then says in words that it is on a different window from the tab. */
const TREND_FALLBACK_PERIOD = "30d" as Period;

const PR_VARIANT_LABEL: Record<string, string> = {
  ABSOLUTE: "Absolute",
  GRID: "Grid",
  WEATHER: "Weather",
  EQUIPMENT: "Equipment",
  GRID_WEATHER_EQUIPMENT: "Grid+Weather+Equipment",
};

/** The ruled display name for each cascade row (chart spec §5.6, SKY-NMH-LOSS-001).
    Exported so the portfolio waterfall (screen 18) names the same rows the same way. */
export const LOSS_ROW_LABEL: Record<string, string> = {
  plan: "Plan",
  weather: "Weather",
  plan_weather_adjusted: "Plan, weather-adjusted",
  plan_allowance: "Plan allowance",
  potential: "Potential",
  grid_outage: "Grid outage",
  grid_curtailment: "Grid curtailment",
  downtime: "Downtime",
  available: "Available",
  soiling: "Soiling",
  array: "Array",
  conversion: "Conversion",
  generated: "Generated",
  auxiliary: "Auxiliary",
  evacuation: "Evacuation",
  unattributed: "Unexplained",
  exported: "Exported",
};

export function SitePerformance({ site, period, from, to }: {
  site: string; period?: string; from?: string; to?: string;
}): React.ReactNode {
  const navigate = useNavigate();
  const activePeriod = (period ?? "30d") as Period;
  // Bounds travel only with a custom window; every other period resolves server
  // side, and sending them anyway would let a stale range narrow it silently.
  const windowArgs = activePeriod === "custom" ? { from, to } : {};
  const [prVariant, setPrVariant] = useState<PrVariant>("ABSOLUTE");
  const [availBasis, setAvailBasis] = useState<"PLANT" | "GRID">("PLANT");
  // Which tile's caret is open; "" is all closed. The screen loads neutral — no
  // tile pre-selected, every KPI card equal until the person opens one. (Wireframe
  // 17 drew PR open by default; changed on request so the page opens unbiased.)
  const [openTile, setOpenTile] = useState<TileKey | "">("");
  // An EXPLICIT widening of the trend window, only ever set by the person clicking the
  // button the live-day panel offers. Null means the trend follows the period tab, so
  // the panel never shows a window other than the one selected without being told to.
  const [trendPeriod, setTrendPeriod] = useState<Period | null>(null);
  const hasValueLens = useHasLens("value");

  // One query for the screen, plus one for the selected PR variant (React Query caches
  // per scope/period/variant, so switching a chip re-reads only the PR tile).
  const base = useGetV1Kpi({ scope: site, period: activePeriod, ...windowArgs });
  const prQuery = useGetV1Kpi({
    scope: site, period: activePeriod, pr_variant: prVariant, ...windowArgs });

  // The open tile's daily trend. A tile with no series metric in the contract does not
  // fetch at all — the panel states the reason instead.
  const trendMetric = openTile === "" ? null : metricForTile(openTile, availBasis);
  const effectiveTrendPeriod = trendPeriod ?? activePeriod;
  // The live day's own curve, for a tile that has one. `enabled` keeps this to a
  // single request on the Today tab and none at all on any other.
  const liveSignal = openTile === "" ? undefined : LIVE_DAY_SIGNAL[openTile];
  const onLiveTab = activePeriod === "today" && trendPeriod === null;

  const trend = useGetV1KpiSeries(
    {
      metric: trendMetric ?? "energy", scope: site, period: effectiveTrendPeriod,
      // Only when the trend is still following a custom tab, not when it has been
      // explicitly widened to a different window.
      ...(effectiveTrendPeriod === "custom" ? { from, to } : {}),
    },
    { query: { enabled: trendMetric !== null && !(onLiveTab && liveSignal) } },
  );

  // Instants, not the dates beside them: a telemetry read takes a half-open
  // range, and on the live day the two dates are the same (meta.period).
  const liveWindow = base.data?.status === 200
    ? (base.data.data.meta?.period as MetaPeriod | undefined)
    : undefined;
  const liveTrend = useGetV1Series(
    {
      site, signal: liveSignal ?? "",
      from: liveWindow?.start_at ?? "", to: liveWindow?.end_at ?? "",
    },
    { query: { enabled: Boolean(
        onLiveTab && liveSignal && liveWindow?.start_at && liveWindow?.end_at) } },
  );

  const setPeriod = (key: Period) => {
    // A new tab resets the widening, so the trend goes back to following the tab.
    setTrendPeriod(null);
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      // Leaving a custom window drops its bounds: they belong to that window only.
      ...prev, period: key, ...(key === "custom" ? {} : { from: undefined, to: undefined }),
    })) as never });
  };

  /** The navigator stepped the window: that IS a custom range. */
  const setWindow = (f: string, t: string) => {
    setTrendPeriod(null);
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      ...prev, period: "custom", from: f, to: t,
    })) as never });
  };

  if (base.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        <Skeleton variant="text" width="16rem" />
        <div className="sk-sp-tiles">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="tile" />)}
        </div>
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }

  if (base.isError || !base.data || base.data.status !== 200) {
    return (
      <ErrorState
        title="Performance is unavailable"
        message="The KPI view for this plant could not be loaded. Nothing is shown rather than stale or partial figures."
        onRetry={() => void base.refetch()}
      />
    );
  }

  const env = base.data.data;
  const d: EnvelopeKpiData = env.data;
  const rows: LossCascadeRow[] = d.loss_cascade ?? [];
  const periodLine = periodLabel(env.meta?.period);
  // The window the server resolved for this read — what the navigator must show.
  const resolvedWindow = env.meta?.period as { start?: string; end?: string } | undefined;
  const selectedPr = prQuery.data?.status === 200 ? prQuery.data.data.data.pr?.selected : undefined;
  const variants = (d.pr?.variants ?? []) as PrVariant[];

  // One tile open at a time (UX-006 §5.17). Re-clicking the open tile closes it.
  const toggleTile = (key: TileKey) => setOpenTile((cur) => (cur === key ? "" : key));

  const trendSeries: EnvelopeKpiSeriesData | undefined =
    trend.data?.status === 200 ? trend.data.data.data : undefined;
  // The window the SERVER resolved for the trend, so the panel never names a window of
  // its own invention, and the basis it stated for the metric.
  const trendWindowLine =
    trend.data?.status === 200 ? periodLabel(trend.data.data.meta?.period) : periodLabel(null);
  const trendBasis = trendSeries?.basis ?? null;

  const hasAnyFigure =
    typeof d.energy_delivered?.value === "number" ||
    typeof d.pr?.selected?.value === "number" ||
    rows.some((r) => typeof r.figure?.value === "number");

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        // The route's site identifier, never a plant or customer name (CLAUDE.md rule 5).
        title={`${site} · Performance`}
        scope={periodLine}
        timeControl="none"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="sk-scenario" role="group" aria-label="Time period">
              {PERIOD_TABS.map((t) => (
                <button key={t.key} type="button" data-active={activePeriod === t.key ? "true" : "false"} onClick={() => setPeriod(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* Annotation 1: the navigator beside the tabs. The arrows step one
                period, the label opens a From/To picker, and forward is disabled
                when the window already ends at the latest day served. It shows
                the window the SERVER resolved, not one worked out from the
                browser clock — those differ, and the control must not name a
                window the tiles below it are not showing. */}
            <DateNav
              period={activePeriod}
              from={from}
              to={to}
              resolved={{ from: resolvedWindow?.start, to: resolvedWindow?.end }}
              onNavigate={setWindow}
            />
            <button type="button" style={exportBtnStyle} onClick={() => exportCsv(site, periodLine, d, rows)}>
              Export
            </button>
          </div>
        }
      />
      <SiteTabBar site={site} />

      {!hasAnyFigure ? (
        <EmptyState
          variant="no_data"
          title="No computed day for this plant in this period"
          message="The daily engine has produced nothing for the selected window, so there is nothing to state."
        />
      ) : null}

      {/* ── Row 1: six KPI tiles, every one a server figure ── */}
      <section aria-labelledby="sp-tiles">
        <h2 id="sp-tiles" className="sr-only">KPI tiles</h2>
        <div className="sk-sp-tiles">
          <FigureTile
            label="Energy delivered"
            figure={d.energy_delivered}
            showBasis
            maximumFractionDigits={1}
            trend={{ open: openTile === "energy", onToggle: () => toggleTile("energy") }}
          >
            {hasValueLens && d.revenue ? <Money money={d.revenue} /> : null}
          </FigureTile>

          {/* PR with the server's own variant list; a variant it cannot compute arrives
              withheld with its reason and prints that, not another variant's number. */}
          <div {...tileProps("PR", openTile === "pr", () => toggleTile("pr"))}>
            <Caret open={openTile === "pr"} />
            <span className="sk-tile__label">PR · {PR_VARIANT_LABEL[prVariant] ?? prVariant}</span>
            {prQuery.isLoading ? (
              <Skeleton variant="text" width="5rem" />
            ) : selectedPr ? (
              <Metric figure={selectedPr} showBasis={false} showProvenance={false} showFreshness={false} />
            ) : (
              <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_VARIANT_NOT_SERVED" }} />
            )}
            <span className="sk-tile__sub">
              {variants.length > 0 ? (
                // UX-REV-002 M3: a summary tile carries ONE figure and a compact way to
                // switch basis, not all five variants laid out as chips. The five-way
                // side-by-side comparison keeps its home on the fleet Performance page.
                // stopPropagation so operating the switch never toggles the tile trend.
                <label className="basis-switch" onClick={(e) => e.stopPropagation()}>
                  <span className="muted">basis</span>
                  <select
                    aria-label="PR basis"
                    value={prVariant}
                    onChange={(e) => { e.stopPropagation(); setPrVariant(e.target.value as PrVariant); }}
                    style={{ fieldSizing: "content" } as React.CSSProperties}
                  >
                    {variants.map((v) => (
                      <option key={v} value={v}>{PR_VARIANT_LABEL[v] ?? v}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_VARIANT_LIST" }} />
              )}
            </span>
          </div>

          <FigureTile
            label="CUF (AC)"
            figure={d.cuf?.ac}
            sub="contracted AC · uncapped"
            trend={{ open: openTile === "cuf", onToggle: () => toggleTile("cuf") }}
          />

          {/* Ruling §6.1: availability is never bare. The tile names which of the three
              it shows, and the engine-owned energy-by-cause figure sits beside it. */}
          <div
            {...tileProps(
              availBasis === "GRID" ? "Grid availability" : "Technical availability",
              openTile === "avail",
              () => toggleTile("avail"),
            )}
          >
            <Caret open={openTile === "avail"} />
            <span className="sk-tile__label">
              {availBasis === "GRID" ? "Grid availability" : "Technical availability"}
            </span>
            {(() => {
              const fig = availBasis === "GRID" ? d.availability?.grid : d.availability?.technical;
              return fig ? (
                <Metric figure={fig} showBasis={false} showProvenance={false} showFreshness={false} />
              ) : (
                <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
              );
            })()}
            <span className="sk-tile__sub">
              <span className="sk-prbasis" role="group" aria-label="Availability basis">
                {(["PLANT", "GRID"] as const).map((b) => (
                  <button
                    key={b}
                    type="button"
                    className="sk-prbasis__chip"
                    data-active={b === availBasis ? "true" : "false"}
                    onClick={(e) => { e.stopPropagation(); setAvailBasis(b); }}
                  >
                    {b === "PLANT" ? "Technical" : "Grid"}
                  </button>
                ))}
              </span>
              <span>
                {"· energy-by-cause "}
                {d.availability?.energy_by_cause ? (
                  <Metric figure={d.availability.energy_by_cause} showBasis={false} showProvenance={false} showFreshness={false} />
                ) : (
                  <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
                )}
              </span>
            </span>
          </div>

          <FigureTile
            label="Specific yield"
            figure={d.specific_yield}
            maximumFractionDigits={2}
            trend={{ open: openTile === "yield", onToggle: () => toggleTile("yield") }}
          />
          <FigureTile
            label="CO₂ avoided"
            figure={d.co2_avoided}
            maximumFractionDigits={1}
            trend={{ open: openTile === "co2", onToggle: () => toggleTile("co2") }}
          />
        </div>
      </section>

      {/* ── The open tile's daily trend, in place below the tile row ── */}
      {openTile !== "" ? (
        <div className="sk-trend">
          <div className="sk-trend__head">
            <div>
              <b>{TILE_TREND_TITLE[openTile]}</b>
              <span className="sk-trend__sub">
                {onLiveTab && liveSignal
                  ? `today, to now · ${trendWindowLine}`
                  : trendMetric === null
                    ? "no daily series for this basis"
                    : `per completed day · ${trendWindowLine}`}
              </span>
            </div>
            <div className="sk-trend__right">
              {trendBasis ? <span className="sk-basis-chip">{trendBasis}</span> : null}
              <button type="button" style={smallBtnStyle} onClick={() => void navigate({ to: "../explorer" as never })}>
                Open in explorer
              </button>
              <button type="button" style={smallBtnStyle} onClick={() => setOpenTile("")}>
                Close
              </button>
            </div>
          </div>
          {onLiveTab && liveSignal ? (
            <LiveDayBody
              label={TILE_TREND_TITLE[openTile]}
              loading={liveTrend.isLoading}
              data={liveTrend.data?.status === 200 ? liveTrend.data.data.data : undefined}
            />
          ) : (
          <TrendBody
            metric={trendMetric}
            label={TILE_TREND_TITLE[openTile]}
            loading={trendMetric !== null && trend.isLoading}
            failed={trendMetric !== null && (trend.isError || (trend.data != null && trend.data.status !== 200))}
            onRetry={() => void trend.refetch()}
            series={trendSeries}
            /* The window the trend is actually on, and whether the person widened it. */
            windowLine={trendWindowLine}
            widened={trendPeriod !== null && trendPeriod !== activePeriod}
            tabPeriodLabel={PERIOD_TABS.find((t) => t.key === activePeriod)?.label ?? activePeriod}
            onWiden={() => setTrendPeriod(TREND_FALLBACK_PERIOD)}
            onFollowTab={() => setTrendPeriod(null)}
            widenLabel={PERIOD_TABS.find((t) => t.key === TREND_FALLBACK_PERIOD)?.label ?? TREND_FALLBACK_PERIOD}
            /* PR is served on one stored basis; the variant chips do not change it. */
            basisNote={
              openTile === "pr" && prVariant !== "ABSOLUTE"
                ? `The daily PR series is served on the basis the server states above, not on the ${PR_VARIANT_LABEL[prVariant] ?? prVariant} variant selected on the tile.`
                : null
            }
          />
          )}
        </div>
      ) : null}

      {/* ── Row 2: the withheld panels | the loss waterfall ── */}
      <div className="sk-sp-cols">
        <section aria-labelledby="sp-levels" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="sp-levels" className="sk-section__title">Energy levels by day</h2>
              <span className="sk-section__sub">potential, available and generated per day</span>
            </div>
          </div>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_DAILY_LADDER_SERIES" }} />
          <p className="sk-sp-note">
            The daily ladder is served as one aggregate for the period, not as a per-day series,
            so the three levels cannot be drawn day by day.
          </p>

          <h3 className="sk-sp-subhead">Downtime <span>· by device and cause</span></h3>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_DOWNTIME_BY_CAUSE" }} />
          <p className="sk-sp-note">
            Downtime is not yet broken out by device and cause for this period.
          </p>

          <h3 className="sk-sp-subhead">Performance index</h3>
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_PERFORMANCE_INDEX" }} />
          <p className="sk-sp-note">
            A single headline performance index is not shown here. Performance is reported
            above as PR and CUF, each on its own stated basis.
          </p>
        </section>

        <section aria-labelledby="sp-wf" className="sk-section">
          <div className="sk-section__head" style={{ flexWrap: "wrap" }}>
            <div>
              <h2 id="sp-wf" className="sk-section__title" style={{ whiteSpace: "nowrap" }}>Loss waterfall</h2>
              <span className="sk-section__sub">{periodLine} · as delivered by the engine</span>
            </div>
          </div>
          <LossWaterfall rows={rows} hasValueLens={hasValueLens} />
        </section>
      </div>
    </div>
  );
}

/* ── The caret affordance: the whole tile is the control that opens its trend ──
   `role=button` + `tabIndex` + Enter/Space, with `aria-expanded` naming the disclosure.
   The chips that live in a tile's sub-line are real buttons of their own, so the keyboard
   handler only fires for the tile itself and the chips stop their click propagating. */
function tileProps(label: string, open: boolean, onToggle: () => void) {
  return {
    className: `sk-tile sk-tile--clickable${open ? " open" : ""}`,
    role: "button",
    tabIndex: 0,
    "aria-expanded": open,
    "aria-label": `${label} trend`,
    onClick: onToggle,
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        e.preventDefault();
        onToggle();
      }
    },
  } as const;
}

function Caret({ open }: { open: boolean }): React.ReactNode {
  return (
    <span className={`caret${open ? " on" : ""}`} aria-hidden="true">
      {open ? "▴" : "▾"}
    </span>
  );
}

/* ── A KPI tile for one server figure ── */
function FigureTile(props: {
  label: string;
  figure?: Figure;
  sub?: string;
  showBasis?: boolean;
  maximumFractionDigits?: number;
  children?: React.ReactNode;
  /** The caret's disclosure state and toggle. Omitted = a plain, non-clickable tile. */
  trend?: { open: boolean; onToggle: () => void };
}): React.ReactNode {
  return (
    <div {...(props.trend ? tileProps(props.label, props.trend.open, props.trend.onToggle) : { className: "sk-tile" })}>
      {props.trend ? <Caret open={props.trend.open} /> : null}
      <span className="sk-tile__label">{props.label}</span>
      {props.figure ? (
        <Metric
          figure={props.figure}
          showBasis={props.showBasis ?? false}
          showProvenance={false}
          showFreshness={false}
          maximumFractionDigits={props.maximumFractionDigits}
        />
      ) : (
        <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
      )}
      {props.sub || props.children ? (
        <span className="sk-tile__sub">
          {props.sub ? <span>{props.sub}</span> : null}
          {props.children}
        </span>
      ) : null}
    </div>
  );
}

/* ── The body of the open tile's trend panel ──
   Four honest outcomes, and the panel plus its controls survive all of them:
     · the tile's figure has no series metric in the contract → state word + reason;
     · the whole series is unavailable for a stated reason (the live day, or a ratio at
       fleet scope) → that state word and that reason, and the live day offers to widen;
     · the series has points but no day carries a figure → the days' own reasons, counted;
     · the series has points → the line, breaking at every withheld day, and a footnote
       naming what each gap is.
   Nothing here computes anything: no rolling mean, no cumulative, no fitted rate. */
function TrendBody(props: {
  metric: GetV1KpiSeriesMetric | null;
  label: string;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  series?: EnvelopeKpiSeriesData;
  windowLine: string;
  widened: boolean;
  tabPeriodLabel: string;
  onWiden: () => void;
  onFollowTab: () => void;
  widenLabel: string;
  basisNote: string | null;
}): React.ReactNode {
  if (props.metric === null) {
    return (
      <div>
        <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_KPI_SERIES_ENDPOINT" }} />
        <p className="sk-sp-note">
          A day-by-day series is available for technical availability, not for grid
          availability. Switch the basis chip back to Technical to see the series that exists,
          rather than drawing the technical line under a grid heading.
        </p>
      </div>
    );
  }

  if (props.loading) return <Skeleton variant="row" lines={4} />;

  if (props.failed || !props.series) {
    return (
      <div>
        <p className="sk-sp-note">
          The daily series for this tile could not be read. Nothing is drawn rather than a
          partial or stale curve.
        </p>
        <button type="button" style={smallBtnStyle} onClick={props.onRetry}>Retry</button>
      </div>
    );
  }

  const s = props.series;
  const points = s.points ?? [];
  // Revenue carries its amount in the currency field, not in `unit` (the contract implies
  // no currency symbol anywhere). Either way the axis label is a server field, never a
  // literal in this file.
  const axisUnit = s.unit || s.currency || "";

  const widenControls = (
    <>
      {props.widened ? (
        <p className="sk-sp-note">
          This trend is on <b>{props.windowLine}</b>, a wider window than the{" "}
          <b>{props.tabPeriodLabel}</b> tab above — you asked for it, and it is labelled here
          so the two are never confused.{" "}
          <button type="button" style={smallBtnStyle} onClick={props.onFollowTab}>
            Follow the {props.tabPeriodLabel} tab
          </button>
        </p>
      ) : (
        <p className="sk-sp-note">
          <button type="button" style={smallBtnStyle} onClick={props.onWiden}>
            Show the last {props.widenLabel} instead
          </button>{" "}
          — completed days only, and the panel will say it is on a different window from the tab.
        </p>
      )}
    </>
  );

  if (points.length === 0) {
    const reason = s.reason ?? null;
    const isLiveDay = reason?.template === "CANNOT_CHECK_LIVE_DAY_HAS_NO_COMPLETED_KPI";
    return (
      <div>
        <StateWord state={stateOfTemplate(reason?.template)} reason={reason} />
        <p className="sk-sp-note">
          No completed day in {props.windowLine} carries this figure, so no line is drawn.
          The reason above is the server's own, not an empty chart dressed up as one.
        </p>
        {isLiveDay ? widenControls : null}
      </div>
    );
  }

  const days = points.map((p) => p.day);
  // A withheld day is `null` here and NOTHING else: not 0, not the day before, and not
  // removed from the axis. KpiTrend breaks the line on a null.
  const values = points.map((p) => (typeof p.figure.value === "number" ? p.figure.value : null));
  const withheld = points.filter((p) => typeof p.figure.value !== "number");
  const drawn = points.length - withheld.length;

  // Why each gap is a gap, grouped so a 18-day hole reads as one statement, not 18.
  const byReason = new Map<string, { count: number; figure: Figure }>();
  for (const p of withheld) {
    const key = `${p.figure.state ?? "NOT_MEASURED"}|${p.figure.reason?.template ?? ""}`;
    const seen = byReason.get(key);
    if (seen) seen.count += 1;
    else byReason.set(key, { count: 1, figure: p.figure });
  }

  if (drawn === 0) {
    return (
      <div>
        <p className="sk-sp-note">
          All {points.length} days in {props.windowLine} are withheld, so there is no line to
          draw. Each day states why:
        </p>
        <ul className="sk-sp-note">
          {[...byReason.entries()].map(([key, g]) => (
            <li key={key}>
              {g.count} of {points.length} days —{" "}
              <StateWord state={g.figure.state ?? "NOT_MEASURED"} reason={g.figure.reason} />
            </li>
          ))}
        </ul>
        {props.widened ? widenControls : null}
      </div>
    );
  }

  return (
    <>
      <KpiTrend
        unit={axisUnit}
        days={days}
        lines={[{ label: DAILY_LINE_LABEL, kind: "measured", values }]}
        height={TREND_HEIGHT}
        ariaLabel={`${props.label} over ${props.windowLine}`}
        summary={
          `${props.label}: ${drawn} of ${points.length} days carry a figure` +
          (axisUnit ? ` in ${axisUnit}` : "") +
          `. ${withheld.length} days are withheld and the line breaks at each of them.`
        }
      />
      {withheld.length > 0 ? (
        <div className="sk-sp-note">
          <p>
            The line breaks on {withheld.length} of {points.length} days. A withheld day is
            not zero, so it is left open rather than drawn to the floor:
          </p>
          <ul>
            {[...byReason.entries()].map(([key, g]) => (
              <li key={key}>
                {g.count} {g.count === 1 ? "day" : "days"} —{" "}
                <StateWord state={g.figure.state ?? "NOT_MEASURED"} reason={g.figure.reason} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {props.basisNote ? <p className="sk-sp-note">{props.basisNote}</p> : null}
      {/* UX-006 §5.16 draws three lines — daily, 7-day rolling and cumulative. Only the
          daily figure is served; a rolling mean or a cumulative total computed here would
          be a KPI invented in the browser (CLAUDE.md rule 2), and a degradation rate read
          off the trend is an engine figure, so no slope is fitted either. */}
      <p className="sk-sp-note">
        Only the daily figure recorded for each completed day is drawn. A 7-day rolling
        average, a running cumulative total, and a trend rate are not shown on this chart.
      </p>
      {props.widened ? widenControls : null}
    </>
  );
}

/** The state word a reason template belongs to; the template names its own state. */
function stateOfTemplate(template?: string): NonNullable<Figure["state"]> {
  return template?.startsWith("CANNOT_CHECK") ? "CANNOT_CHECK" : "NOT_MEASURED";
}

/* ── The live day's own curve ───────────────────────────────────────────────
   Today has no completed day, so there is no daily KPI for it — but the energy
   register is read right through the day. This draws it to now, on the clock,
   which is what "today" ought to show instead of an empty completed-day panel.

   The figures are the server's: this reads points, it does not integrate power
   or derive a running total (rule 2). A reading the platform withheld — the
   register's first interval of the local day still carries yesterday's close,
   and the series read says so — breaks the line rather than reading as zero. */
function LiveDayBody({ label, loading, data }: {
  label: string;
  loading: boolean;
  data?: EnvelopeSeriesData;
}): React.ReactNode {
  if (loading) return <Skeleton variant="row" lines={4} />;
  const points = (data?.points ?? []) as { ts: string; value?: number | null }[];
  const drawn = points.filter((p) => typeof p.value === "number").length;
  if (drawn === 0) {
    return (
      <div>
        <StateWord state="CANNOT_CHECK"
                   reason={(data?.reason as never) ?? { template: "CANNOT_CHECK_NO_LIVE_SOURCE" }} />
        <p className="sk-sp-note">
          Nothing has been read for this site today yet, so there is no curve. The
          reason above is the server&rsquo;s own, not an empty chart dressed up as one.
        </p>
      </div>
    );
  }
  const unit = data?.unit ?? "";
  return (
    <>
      <KpiTrend
        unit={unit}
        days={points.map((p) => p.ts)}
        formatX={(iso) => {
          const t = new Date(iso);
          return Number.isNaN(t.getTime()) ? iso
            : t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
        }}
        lines={[{
          label: "today",
          kind: "measured",
          values: points.map((p) => (typeof p.value === "number" ? p.value : null)),
        }]}
        height={TREND_HEIGHT}
        ariaLabel={`${label} through today`}
        summary={`${label}: ${drawn} of ${points.length} intervals carry a figure${
          unit ? ` in ${unit}` : ""}. The day is still running, so the last point is the latest reading, not a total.`}
      />
      <p className="sk-sp-note">
        The day is still running: the last point is the latest reading, not a day&rsquo;s
        total. Scrub the line for the time and the figure.
      </p>
    </>
  );
}

/* ── The 17-row loss waterfall, through the shared LossCascade component ──
   Chart spec §5.6: if POTENTIAL carries no figure there is no waterfall at all — the
   card prints the state word and the reason. Nothing here is derived: the rows, their
   order and their statuses are those the engine delivered. */
function LossWaterfall(
  { rows, hasValueLens }: { rows: LossCascadeRow[]; hasValueLens: boolean },
): React.ReactNode {
  if (rows.length === 0) {
    return (
      <EmptyState
        variant="no_data"
        title="No loss cascade for this period"
        message="The daily ladder has no rows for the selected window."
      />
    );
  }

  const potential = rows.find((r) => r.label === "potential");
  if (typeof potential?.figure?.value !== "number") {
    return (
      <div>
        <StateWord
          state={potential?.figure?.state ?? "NOT_MEASURED"}
          reason={potential?.figure?.reason ?? { template: "NOT_MEASURED_ENGINE_ABSENT" }}
        />
        <p className="sk-sp-note">
          The loss breakdown is not available for this period yet. Each band is a share of
          the period total potential, and that figure has not been produced yet, so the
          steps cannot be drawn.
        </p>
      </div>
    );
  }

  const unit = rows.find((r) => r.figure?.unit)?.figure?.unit ?? "";
  const byLabel = new Map<string, LossCascadeRow>();
  const steps: CascadeStep[] = rows.map((r) => {
    const label = displayLabel(r.label);
    byLabel.set(label, r);
    return {
      label,
      value: typeof r.figure?.value === "number" ? r.figure.value : null,
      status: mapRowStatus(r.row_status),
    };
  });

  const withheld = rows.filter((r) => typeof r.figure?.value !== "number");

  return (
    <>
      <LossCascade
        unit={unit}
        steps={steps}
        renderValue={(s) => {
          const row = byLabel.get(s.label);
          if (hasValueLens && row?.money) return <Money money={row.money} />;
          return row?.figure ? <Metric figure={row.figure} showProvenance={false} showFreshness={false} /> : null;
        }}
      />
      {withheld.length > 0 ? (
        <div className="sk-sp-note">
          <p>
            {withheld.length} of {rows.length} rows carry no figure. Each names what is missing:
          </p>
          <ul>
            {withheld.map((r) => (
              <li key={r.label}>
                {displayLabel(r.label)} — <StateWord state={r.figure?.state ?? "NOT_MEASURED"} reason={r.figure?.reason} />
                {engineOf(r) ? ` · ${engineOf(r)}` : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {/* The share-of-potential headline and the lost-energy total are engine figures.
          /v1/kpi does not carry them and the browser must not compute them, so the card
          states no headline rather than a derived one (CLAUDE.md rule 2). */}
      <p className="sk-sp-note">
        Share of potential exported:{" "}
        <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_CASCADE_HEADLINE" }} />
      </p>
    </>
  );
}

/** The engine the ladder named as missing for a row, when it named one. */
export function engineOf(row?: LossCascadeRow): string | null {
  const params = row?.figure?.reason?.params as Record<string, unknown> | undefined;
  const one = params?.["engine"];
  if (typeof one === "string" && one) return one;
  const many = params?.["engines"];
  if (Array.isArray(many) && many.length > 0) return many.filter((x) => typeof x === "string").join(", ");
  return null;
}

export function displayLabel(label?: string): string {
  if (!label) return "Unnamed row";
  return LOSS_ROW_LABEL[label] ?? label.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** The backend's ladder status vocabulary, and the contract's, mapped to the chart's. */
function mapRowStatus(status?: RowStatus | string): CascadeRowStatus {
  switch (status) {
    case "engine":
    case "measured":
      return "engine";
    // The status the backend sends for every measured row. Without this case it
    // fell to the default and the screen said "not measured" next to a real MWh.
    case "platform":
      return "platform";
    case "platform_estimate":
    case "declared":
      return "platform_estimate";
    default:
      return "NOT_MEASURED";
  }
}

/* The period the server resolved. The backend anchors every period to the latest data
   day, so the browser clock never names the window (it would read a lagging feed as
   today, and a stepper built on it would ask for days the query never sent). */
export function periodLabel(period?: MetaPeriod | null): string {
  if (!period?.start) return "Period not stated by the server";
  const start = fmtIsoDay(period.start);
  const end = period.end ? fmtIsoDay(period.end) : null;
  return !end || end === start ? start : `${start} to ${end}`;
}

/** Format a YYYY-MM-DD day from the server without crossing a timezone. */
function fmtIsoDay(iso: string): string {
  const [y, m, dd] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !dd) return iso;
  return new Date(y, m - 1, dd).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** Download the Performance view as a CSV. A withheld figure exports its state word. */
function exportCsv(site: string, periodLine: string, d: EnvelopeKpiData, rows: LossCascadeRow[]): void {
  const num = (f?: Figure) => (typeof f?.value === "number" ? String(f.value) : "");
  const state = (f?: Figure) => (f ? f.state ?? "" : "CANNOT_CHECK");
  const unit = (f?: Figure) => f?.unit ?? "";
  const kpis: [string, Figure | undefined][] = [
    ["Energy delivered", d.energy_delivered],
    ["PR (selected variant)", d.pr?.selected],
    ["CUF (AC, uncapped)", d.cuf?.ac],
    ["Technical availability", d.availability?.technical],
    ["Grid availability", d.availability?.grid],
    ["Energy availability by cause", d.availability?.energy_by_cause],
    ["Specific yield", d.specific_yield],
    ["CO2 avoided", d.co2_avoided],
  ];
  const csvRows: string[][] = [
    [`${site} · Performance`, periodLine],
    [],
    ["KPI", "Value", "Unit", "State"],
    ...kpis.map(([label, f]) => [label, num(f), unit(f), state(f)]),
    [],
    ["Loss cascade row", "Value", "Unit", "State", "Row status", "Missing engine"],
    ...rows.map((r) => [
      displayLabel(r.label),
      num(r.figure),
      unit(r.figure),
      state(r.figure),
      String(r.row_status ?? ""),
      engineOf(r) ?? "",
    ]),
  ];
  const csv = csvRows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `performance-${site}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/* The trend panel's own small controls (Disha's trend-panel button weight). */
const smallBtnStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: 22, padding: "0 8px",
  fontSize: "11px", fontWeight: 500, border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)", background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-2)", cursor: "pointer", whiteSpace: "nowrap",
};

const exportBtnStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: 30, padding: "0 12px",
  fontSize: "12px", fontWeight: 600, border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)", background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)", cursor: "pointer", whiteSpace: "nowrap",
};
